import InteractiveRedirector from "@components/InteractiveRedirector";
import {
  LazyAppLayout,
  LazyBuilder,
  LazyHome,
  LazyLogin,
  LazyNetworkGuard,
  LazyProtectedRoute,
  LazySectionPreview,
  LazySSOCallback,
} from "@components/LazyComponents";
import LoadingSpinner from "@components/LoadingSpinner";
import NotFoundPage from "@components/NotFoundPage";
import Redirector from "@components/Redirector";
import { Suspense } from "react";
import { Route } from "react-router";
import { RouteConfig } from "./routes.config";

// Loading fallback component
const RouteLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="large" text="Loading page..." />
  </div>
);

// Component mapping for string-based component names
const componentMap = {
  LazyLogin,
  LazySSOCallback,
  LazyHome,
  LazyBuilder,
  LazySectionPreview,
  LazyAppLayout,
  LazyProtectedRoute,
  LazyNetworkGuard,
} as const;

interface RouteElementProps {
  config: RouteConfig;
}

const RouteElement = ({ config }: RouteElementProps) => {
  // Handle special Redirector case
  if (config.isRedirector) {
    return <Redirector />;
  }

  // Handle interactive redirector case
  if (config.component === "RedirectToInteractive") {
    return <InteractiveRedirector />;
  }

  // Handle not found redirect case
  if (config.isNotFound) {
    return <NotFoundPage />;
  }

  // Get component from mapping
  const Component = config.component
    ? componentMap[config.component as keyof typeof componentMap]
    : null;

  if (!Component) {
    return <div>Component not found: {config.component}</div>;
  }

  const element = (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Component />
    </Suspense>
  );

  // Wrap with NetworkGuard if required
  if (config.requiresNetwork) {
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <LazyNetworkGuard>{element}</LazyNetworkGuard>
      </Suspense>
    );
  }

  return element;
};

interface RouteRendererProps {
  routes: RouteConfig[];
}

// Function that returns route elements (not a React component)
export const RouteRenderer = ({ routes }: RouteRendererProps) => {
  const renderRoute = (config: RouteConfig, index: number) => {
    // Handle routes with children (nested routes)
    if (config.children) {
      if (config.protected && config.layout) {
        // Protected route with layout
        return (
          <Route
            key={index}
            element={
              <RouteElement config={{ component: "LazyProtectedRoute" }} />
            }
          >
            <Route
              element={<RouteElement config={{ component: "LazyAppLayout" }} />}
            >
              {config.children.map((child, childIndex) =>
                renderRoute(child, childIndex),
              )}
            </Route>
          </Route>
        );
      }

      // Regular nested routes
      return (
        <Route key={index} path={config.path}>
          {config.children.map((child, childIndex) =>
            renderRoute(child, childIndex),
          )}
        </Route>
      );
    }

    // Regular route
    return (
      <Route
        key={index}
        path={config.path}
        element={<RouteElement config={config} />}
      />
    );
  };

  return routes.map((route, index) => renderRoute(route, index));
};
